import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { auditService } from '../../services/auditService';
import { AuditListParams, AuditSummaryResponse } from '../../types/audit';
import { isAbortError } from '../../utils/abortError';

type RangePreset = '1h' | '24h' | '7d' | '30d';

interface Props {
  refreshKey?: number;
  /** When given, the widgets drill down into the Events tab with these filters. */
  onDrillDown?: (filters: Partial<AuditListParams>) => void;
}

const PRESETS: Array<{ key: RangePreset; hours: number }> = [
  { key: '1h', hours: 1 },
  { key: '24h', hours: 24 },
  { key: '7d', hours: 24 * 7 },
  { key: '30d', hours: 24 * 30 },
];

function rangeFor(preset: RangePreset): { from: string; to: string } {
  const now = new Date();
  const hours = PRESETS.find((p) => p.key === preset)?.hours ?? 24;
  const from = new Date(now.getTime() - hours * 3600 * 1000);
  return { from: from.toISOString(), to: now.toISOString() };
}

const Kpi: React.FC<{ value: string; label: string; tone?: 'bad' | 'warn'; hint?: string; onClick?: () => void; testId?: string }> = ({ value, label, tone, hint, onClick, testId }) => {
  const cls = `dash-kpi aud-kpi${tone ? ` is-${tone}` : ''}${onClick ? ' is-clickable' : ''}`;
  const body = (
    <>
      <span className="dash-kpi-label">{label}</span>
      <span className="dash-kpi-value">{value}</span>
      {hint && <span className="dash-kpi-meta">{hint}</span>}
    </>
  );
  return onClick
    ? <button type="button" className={cls} onClick={onClick} data-testid={testId}>{body}</button>
    : <div className={cls} data-testid={testId}>{body}</div>;
};

const BarRow: React.FC<{ label: string; value: number; max: number; onClick?: () => void; valueText: string }> = ({ label, value, max, onClick, valueText }) => {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  const inner = (
    <>
      <span className="aud-bar-label" title={label}>{label}</span>
      <span className="aud-bar-track"><span className="aud-bar-fill" style={{ width: `${pct}%` }} /></span>
      <span className="aud-bar-count">{valueText}</span>
    </>
  );
  return onClick
    ? <button type="button" className="aud-bar-row is-clickable" onClick={onClick}>{inner}</button>
    : <div className="aud-bar-row">{inner}</div>;
};

/**
 * The audit dashboard over a period: the four figures, who did the
 * most, which actions, the activity over time, and the latest failures.
 * Everything drills down into the event list.
 */
export const AuditDashboard: React.FC<Props> = ({ refreshKey, onDrillDown }) => {
  const { t, formatDateTime, formatNumber } = useI18n();
  const [preset, setPreset] = useState<RangePreset>('24h');
  const [excludeReads, setExcludeReads] = useState(true);
  const [authenticatedOnly, setAuthenticatedOnly] = useState(true);
  const [data, setData] = useState<AuditSummaryResponse | null>(null);
  // The window the shown data was built from, so drill-downs reuse it.
  const [appliedRange, setAppliedRange] = useState<{ from: string; to: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAbortRef = useRef<AbortController | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { from, to } = rangeFor(preset);
      const response = await auditService.getAuditSummary(
        { from_timestamp: from, to_timestamp: to, exclude_reads: excludeReads, authenticated_only: authenticatedOnly },
        { signal },
      );
      setData(response);
      setAppliedRange({ from, to });
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[AuditDashboard] load failed:', err);
      setError(err instanceof Error ? err.message : t('audit.dashboard.empty'));
      setData(null);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [preset, excludeReads, authenticatedOnly, t]);

  useEffect(() => {
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
    return () => ac.abort();
  }, [load, refreshKey]);

  const handleRefresh = useCallback(() => {
    loadAbortRef.current?.abort();
    const ac = new AbortController();
    loadAbortRef.current = ac;
    load(ac.signal);
  }, [load]);

  const peakInTimeline = useMemo(() => (data?.timeline?.length ? Math.max(...data.timeline.map((b) => b.count)) : 0), [data]);
  const topActorPeak = useMemo(() => (data?.top_actors?.length ? Math.max(...data.top_actors.map((a) => a.count)) : 0), [data]);
  const topActionPeak = useMemo(() => (data?.top_actions?.length ? Math.max(...data.top_actions.map((a) => a.count)) : 0), [data]);

  const failureRate = data && data.totals.events > 0 ? ((data.totals.failed / data.totals.events) * 100).toFixed(1) : '0.0';

  const drillDownRange = useMemo<Partial<AuditListParams>>(() => {
    if (!appliedRange) return {};
    return { from_timestamp: appliedRange.from, to_timestamp: appliedRange.to, exclude_reads: excludeReads || undefined };
  }, [appliedRange, excludeReads]);

  const drillDown = useCallback(
    (extra: Partial<AuditListParams>) => {
      if (!onDrillDown) return;
      onDrillDown({ ...drillDownRange, ...extra });
    },
    [onDrillDown, drillDownRange],
  );

  const bucketLabel = (bucket: string, kind: 'hour' | 'day'): string => {
    const d = new Date(bucket);
    if (Number.isNaN(d.getTime())) return bucket;
    return kind === 'hour'
      ? d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
  };

  return (
    <div className="aud-dashboard" data-testid="audit-dashboard">
      <div className="tpl-card">
        <div className="tpl-toolbar aud-toolbar">
          <div className="aud-segment" role="tablist">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                role="tab"
                aria-selected={preset === p.key}
                className={`aud-segment-btn${preset === p.key ? ' active' : ''}`}
                onClick={() => setPreset(p.key)}
                data-testid={`audit-range-${p.key}`}
              >
                {t(`audit.dashboard.range.${p.key}`)}
              </button>
            ))}
          </div>
          <label className="tpl-check">
            <input type="checkbox" checked={excludeReads} onChange={(e) => setExcludeReads(e.target.checked)} />
            {t('audit.dashboard.excludeReads')}
          </label>
          <label className="tpl-check" title={t('audit.dashboard.authenticatedOnlyHint')}>
            <input type="checkbox" checked={authenticatedOnly} onChange={(e) => setAuthenticatedOnly(e.target.checked)} data-testid="audit-authenticated-only" />
            {t('audit.dashboard.authenticatedOnly')}
          </label>
          <span className="aud-toolbar-hint tpl-muted">{t('audit.dashboard.intro')}</span>
          <button type="button" className="tpl-link" onClick={handleRefresh} disabled={loading}>
            {loading ? t('common.loading') : t('audit.dashboard.refresh')}
          </button>
        </div>
      </div>

      {error && <div className="error-message aud-error" role="alert">{error}</div>}

      {data && (
        <>
          <div className="dash-kpis aud-kpis">
            <Kpi value={formatNumber(data.totals.events)} label={t('audit.dashboard.kpi.totalEvents')} hint={onDrillDown ? t('audit.dashboard.clickHint') : undefined} onClick={onDrillDown ? () => drillDown({}) : undefined} testId="audit-kpi-events" />
            <Kpi value={formatNumber(data.totals.failed)} label={t('audit.dashboard.kpi.failed')} tone="bad" hint={t('audit.dashboard.failureRate', { rate: failureRate })} onClick={onDrillDown ? () => drillDown({ status: 'failed' }) : undefined} testId="audit-kpi-failed" />
            <Kpi value={formatNumber(data.totals.denied)} label={t('audit.dashboard.kpi.denied')} tone="warn" onClick={onDrillDown ? () => drillDown({ status: 'denied' }) : undefined} testId="audit-kpi-denied" />
            <Kpi value={formatNumber(data.totals.unique_actors)} label={t('audit.dashboard.kpi.uniqueActors')} testId="audit-kpi-actors" />
          </div>

          {authenticatedOnly && (data.totals.excluded_anonymous ?? 0) > 0 && (
            <p className="cu-note aud-note" data-testid="audit-anonymous-hidden">
              {t('audit.dashboard.anonymousHidden', { count: formatNumber(data.totals.excluded_anonymous ?? 0) })}
            </p>
          )}

          <div className="aud-grid">
            <section className="cu-section aud-card">
              <header className="cu-section-head">
                <h3>{t('audit.dashboard.topActors')}</h3>
              </header>
              {data.top_actors.length === 0 ? (
                <div className="tpd-empty"><p>{t('audit.dashboard.empty')}</p></div>
              ) : (
                <div className="aud-bars">
                  {data.top_actors.map((a) => (
                    <BarRow key={a.actor_username} label={a.actor_username} value={a.count} max={topActorPeak} valueText={formatNumber(a.count)} onClick={onDrillDown ? () => drillDown({ actor_username: a.actor_username }) : undefined} />
                  ))}
                </div>
              )}
            </section>

            <section className="cu-section aud-card">
              <header className="cu-section-head">
                <h3>{t('audit.dashboard.topActions')}</h3>
              </header>
              {data.top_actions.length === 0 ? (
                <div className="tpd-empty"><p>{t('audit.dashboard.empty')}</p></div>
              ) : (
                <div className="aud-bars">
                  {data.top_actions.map((a) => (
                    <BarRow key={a.action} label={a.action} value={a.count} max={topActionPeak} valueText={formatNumber(a.count)} onClick={onDrillDown ? () => drillDown({ action: a.action }) : undefined} />
                  ))}
                </div>
              )}
            </section>
          </div>

          <section className="cu-section aud-card">
            <header className="cu-section-head">
              <h3>{t('audit.dashboard.timeline')}</h3>
              <p>{t('audit.dashboard.timelineIntro', { bucket: t(`audit.dashboard.bucket.${data.range.bucket}`) })}</p>
            </header>
            {data.timeline.length === 0 ? (
              <div className="tpd-empty"><p>{t('audit.dashboard.empty')}</p></div>
            ) : (
              <div className="aud-timeline" data-testid="audit-timeline">
                <div className="aud-timeline-bars" style={{ gridTemplateColumns: `repeat(${data.timeline.length}, minmax(6px, 1fr))` }}>
                  {data.timeline.map((b) => {
                    const heightPct = peakInTimeline > 0 ? Math.max(3, Math.round((b.count / peakInTimeline) * 100)) : 0;
                    return (
                      <div key={b.bucket} className="aud-timeline-col" title={`${bucketLabel(b.bucket, data.range.bucket)} · ${formatNumber(b.count)}`}>
                        <div className="aud-timeline-bar" style={{ height: `${heightPct}%` }} />
                      </div>
                    );
                  })}
                </div>
                <div className="aud-timeline-axis">
                  <span>{bucketLabel(data.timeline[0].bucket, data.range.bucket)}</span>
                  <span>{bucketLabel(data.timeline[data.timeline.length - 1].bucket, data.range.bucket)}</span>
                </div>
              </div>
            )}
          </section>

          <section className="cu-section aud-card">
            <header className="cu-section-head">
              <h3>{t('audit.dashboard.recentFailures')}{data.recent_failures.length > 0 && <span className="ud-tab-count">{formatNumber(data.recent_failures.length)}</span>}</h3>
            </header>
            {data.recent_failures.length === 0 ? (
              <div className="tpd-empty"><p>{t('audit.dashboard.noFailures')}</p></div>
            ) : (
              <table className="tpd-table aud-failures" data-testid="audit-failures">
                <thead>
                  <tr>
                    <th>{t('audit.table.timestamp')}</th>
                    <th>{t('audit.table.actor')}</th>
                    <th>{t('audit.table.action')}</th>
                    <th>{t('audit.table.resource')}</th>
                    <th>{t('audit.row.errorMessage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_failures.map((f) => {
                    const onClick = onDrillDown
                      ? () => drillDown({ status: 'failed', resource_type: f.resource_type, resource_id: f.resource_id || undefined, action: f.action })
                      : undefined;
                    return (
                      <tr
                        key={f.id}
                        className={onClick ? 'aud-failure-row is-clickable' : 'aud-failure-row'}
                        onClick={onClick}
                        tabIndex={onClick ? 0 : undefined}
                        role={onClick ? 'button' : undefined}
                        onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
                      >
                        <td className="tpl-muted">{f.timestamp ? formatDateTime(f.timestamp) : '–'}</td>
                        <td>{f.actor_username || t('audit.table.anonymous')}</td>
                        <td><code>{f.action}</code></td>
                        <td><span className="type-badge tpl-type">{f.resource_type}</span>{f.resource_id && <span className="aud-resource-id mono">{f.resource_id}</span>}</td>
                        <td className="aud-failure-msg" title={f.error_message || ''}>{f.error_message || '–'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default AuditDashboard;
